import { X, XCircle, ArrowRight } from 'lucide-react';
import { ReactNode, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { Button } from './Button';
import { E2E_SCHEDULING } from '@/constants/e2eSchedulingTestIds';
import type { ModalMobileSheet } from '@/context/ModalContext';

interface ModalDrawerProps {
	isOpen: boolean;
	onCancel?: () => void;
	onSuccess?: () => void;
	children: ReactNode;
	title?: ReactNode;
	cancelText?: string;
	successText?: string;
	disabledConfirm: boolean;
	size?: 'default' | 'wide' | 'xlarge' | 'compact';
	mobileSheet?: ModalMobileSheet;
}

const MODAL_MAX_WIDTH: Record<
	'default' | 'wide' | 'xlarge' | 'compact',
	string
> = {
	compact: 'max-w-sm',
	default: 'max-w-[700px]',
	wide: 'max-w-[1100px]',
	xlarge: 'max-w-[1320px]',
};

const ModalDrawer = ({
	isOpen,
	onCancel,
	onSuccess,
	children,
	title,
	cancelText,
	successText,
	disabledConfirm = false,
	size = 'default',
	mobileSheet = 'default',
}: ModalDrawerProps) => {
	const dialogMaxWidth = MODAL_MAX_WIDTH[size];
	const isCompact = size === 'compact';
	const isTallMobileSheet = mobileSheet === 'tall';
	const buttonContainerRef = useRef<HTMLDivElement>(null);
	const openedAtRef = useRef(0);

	useEffect(() => {
		if (isOpen) {
			openedAtRef.current = Date.now();
			document.body.style.overflow = 'hidden';
			document.body.classList.add('modal-open-scrollbar');
			if (!isCompact) {
				setTimeout(() => {
					buttonContainerRef.current?.scrollIntoView({
						behavior: 'smooth',
						block: 'end',
					});
				}, 100);
			}
		} else {
			document.body.style.overflow = 'auto';
			document.body.classList.remove('modal-open-scrollbar');
		}

		return () => {
			document.body.style.overflow = 'auto';
			document.body.classList.remove('modal-open-scrollbar');
		};
	}, [isOpen, isCompact]);

	const handleCancel = () => {
		onCancel?.();
	};

	const handleBackdropClick = () => {
		if (Date.now() - openedAtRef.current < 300) return;
		handleCancel();
	};

	const handleSuccess = () => {
		onSuccess?.();
	};

	if (!isOpen) return null;

	return (
		<>
			<style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
      `}</style>

			{/* Acima do SessionModal (`z-[10000]`) e de modais locais em portal. */}
			<div className="fixed inset-0 z-[10020]">
				<div
					className="fixed inset-0 z-0 bg-black bg-opacity-50 transition-opacity"
					onClick={handleBackdropClick}
					aria-hidden
				/>

				<div
					className="relative z-10 hidden min-h-screen items-center justify-center px-4 py-10 text-center md:flex lg:py-12"
					onClick={handleBackdropClick}
					role="presentation"
				>
					<div
						className={`relative w-full ${dialogMaxWidth} transform overflow-hidden rounded-3xl bg-white p-[1px] pt-[2px] text-left align-bottom shadow-xl transition-all sm:my-8 sm:align-middle`}
						onClick={(e) => e.stopPropagation()}
						role="dialog"
						aria-modal="true"
					>
						<button
							onClick={handleCancel}
							className="absolute right-5 top-5 z-[60] rounded-full p-1 text-gray-400 transition-colors hover:text-gray-600"
						>
							<span className="sr-only">Close</span>
							<X className="h-6 w-6" />
						</button>

						<div
							data-testid={E2E_SCHEDULING.PAYMENT_MODAL_SCROLL}
							className={
								isCompact
									? 'z-50 overflow-visible p-4 md:p-5'
									: 'custom-scrollbar z-50 max-h-[calc(100vh-64px)] overflow-y-auto p-5 transition-all duration-300 md:max-h-[calc(100vh-96px)] md:px-6 md:pb-9 md:pt-8 lg:pb-10 lg:pt-9'
							}
							style={
								isCompact
									? undefined
									: {
											transitionProperty:
												'max-height, height, width, min-height, min-width, padding',
									  }
							}
						>
							{title && (
								<div
									className={cn(
										'pr-9 pt-1 text-lg font-black leading-tight text-stone-900 transition-all duration-300 md:pr-10 lg:text-xl',
										isCompact ? 'mb-3' : 'mb-6 md:mb-8',
									)}
									style={{
										transitionProperty:
											'height, width, margin, padding, font-size',
									}}
								>
									{title}
								</div>
							)}
							<div
								className="transition-all duration-300"
								style={{
									transitionProperty:
										'height, width, margin, padding',
								}}
							>
								{children}
								{(successText || cancelText) && (
									<div
										ref={buttonContainerRef}
										className="mt-2 justify-between bg-inherit py-3 transition-all duration-300 sm:flex sm:flex-row-reverse"
										style={{
											transitionProperty:
												'height, width, margin, padding',
										}}
									>
										{successText && (
											<Button
												type="button"
												data-testid={
													E2E_SCHEDULING.REVIEW_MODAL_NEXT
												}
												endIcon={
													<ArrowRight className="size-4 shrink-0 transition-transform group-hover:translate-x-0.5" />
												}
												className="group inline-flex h-11 min-w-[160px] items-center justify-center gap-2 rounded-xl bg-stone-900 px-5 text-sm font-black uppercase tracking-wide text-white shadow-sm transition-all duration-300 hover:bg-stone-800 disabled:opacity-50 disabled:hover:bg-stone-900"
												style={{
													transitionProperty:
														'height, width, background, min-width, font-size, padding',
												}}
												onClick={handleSuccess}
												text={successText}
												disabled={disabledConfirm}
											/>
										)}
										{cancelText && (
											<Button
												type="button"
												className="text-dark transition-all duration-300"
												style={{
													transitionProperty:
														'height, width, color, padding, font-size',
												}}
												endIcon={
													<XCircle className="w-4 text-zinc-400" />
												}
												variant="text"
												onClick={handleCancel}
												text={cancelText}
											/>
										)}
									</div>
								)}
							</div>
						</div>
					</div>
				</div>

				<div
					className={cn(
						'fixed inset-x-0 bottom-0 z-10 overflow-hidden bg-zinc-50 px-[1px] pt-[3px] md:hidden',
						isTallMobileSheet ? 'rounded-t-2xl' : 'rounded-t-lg',
					)}
				>
					<div
						data-testid={E2E_SCHEDULING.PAYMENT_MODAL_SCROLL}
						className={cn(
							'custom-scrollbar transform overflow-y-auto bg-inherit shadow-xl transition-all',
							isTallMobileSheet
								? 'max-h-[calc(100dvh-3.25rem)] min-h-[min(92dvh,calc(100dvh-3.25rem))]'
								: 'max-h-[calc(100vh-128px)]',
						)}
						onClick={(e) => e.stopPropagation()}
					>
						<div className="sticky top-0 z-50 flex items-center justify-end border-b bg-zinc-100/95 p-3 px-4">
							{title && (
								<div className="mr-auto text-lg font-black leading-tight text-stone-900">
									{title}
								</div>
							)}
							<button
								onClick={handleCancel}
								className="text-gray-400 hover:text-gray-500"
							>
								<span className="sr-only">Close</span>
								<X className="h-6 w-6" />
							</button>
						</div>
						<div className="px-4 pb-4 pt-6">
							{children}
							{(successText || cancelText) && (
								<div
									ref={buttonContainerRef}
									className="mt-4 w-full flex-col justify-between space-y-4 bg-inherit md:flex-row-reverse"
								>
									{successText && (
										<Button
											type="button"
											data-testid={
												E2E_SCHEDULING.REVIEW_MODAL_NEXT
											}
											endIcon={
												<ArrowRight className="size-4 shrink-0 transition-transform group-hover:translate-x-0.5" />
											}
											className="group h-11 w-full justify-center rounded-xl bg-stone-900 px-5 text-sm font-black uppercase tracking-wide text-white shadow-sm hover:bg-stone-800 disabled:opacity-50 disabled:hover:bg-stone-900"
											onClick={handleSuccess}
											text={successText}
											disabled={disabledConfirm}
										/>
									)}
									{cancelText && (
										<Button
											type="button"
											endIcon={
												<XCircle className="w-4" />
											}
											variant="text"
											onClick={handleCancel}
											text={cancelText}
											className="w-full justify-center py-2 md:w-fit"
										/>
									)}
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default ModalDrawer;
